import React, { useContext, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner, Modal } from 'react-bootstrap';
import { FaTimesCircle, FaSignInAlt, FaShieldAlt } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';

import { AuthContext } from '../context/AuthContext';
import SocialLogin from '../components/SocialLogin';
import '../styles/LoginPage.css';

const BASE_URL = process.env.REACT_APP_API_URL;
const COMPANY_CODE = process.env.REACT_APP_COMPANY_CODE;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, userData } = useContext(AuthContext);
  
  // Get the page the user was trying to access before login
  // Only redirect to 'from' if user was redirected here from a protected route
  const from = location.state?.from?.pathname;
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(null);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [modalEmail, setModalEmail] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSocialLoginSuccess = (token, user, provider) => {
    login(token, user);
    // console.log('token:', token);
    
    // Redirect logic same as regular login
    if (from && location.state?.from) {
      const fullPath = from + (location.state.from.search || '');
      navigate(fullPath, { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  };

  const handleSocialLoginError = (errorMessage) => {
    setErrorMsg(errorMessage);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg('');

    try {
      const response = await fetch(`${BASE_URL}/api/customer/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_code: COMPANY_CODE,
          email: formData.email,
          password: formData.password
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        login(data.token, data.user);
        // Only redirect to 'from' location if user was redirected here from a protected route
        // Otherwise, go to home page for normal logins
        if (from && location.state?.from) {
          const fullPath = from + (location.state.from.search || '');
          navigate(fullPath, { replace: true });
        } else {
          navigate('/', { replace: true });
        }
        // console.log('token:', data.token);
        
      } else {
        setErrorMsg(data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Error logging in:', error);
      setErrorMsg('Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (emailParam) => {
    setForgotMsg('');
    setForgotLoading(true);
    setForgotSuccess(null);
    try {
      const emailToUse = emailParam || (userData && userData.email) ? (emailParam || userData.email) : formData.email;
      if (!emailToUse) {
        setForgotMsg('No email available.');
        setForgotSuccess(false);
        setForgotLoading(false);
        return;
      }

      const res = await fetch(`${BASE_URL}/api/customer/auth/request-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_code: COMPANY_CODE,
          email: emailToUse,
          frontend_url: window.location.origin
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setForgotMsg('Password reset email sent to the registered email address.');
        setForgotSuccess(true);
      } else {
        setForgotMsg(data.message || 'Failed to send reset email.');
        setForgotSuccess(false);
      }
    } catch (err) {
      console.error('Forgot password error', err);
      setForgotMsg('Something went wrong while requesting password reset.');
      setForgotSuccess(false);
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="login-container">
      <Container fluid="xl">
        <Card className="login-card">
          <Card.Body className="p-3 p-md-5">
            <div className="text-center mb-4">
              <div className="login-icon mb-3">
                <FaSignInAlt size={48} className="text-primary" />
              </div>
              <h2 className="login-title">Welcome!</h2>
              <p className="login-subtitle">Sign in to your account to continue shopping</p>
            </div>

            {errorMsg && (
              <Alert variant="danger" className="modern-alert">
                <div className="d-flex align-items-center justify-content-center mb-2">
                  <FaTimesCircle className="me-2" />
                  <strong>Login Failed</strong>
                </div>
                {errorMsg}
                {errorMsg.toLowerCase().includes('invalid') && (
                  <div className="mt-2">
                    <small className="text-muted">
                      Having trouble? Try{' '}
                      <span className="link-text">resetting your password</span>
                    </small>
                  </div>
                )}
              </Alert>
            )}

            <Row className="g-4">
              <Col lg={7} md={12} >
                <div className="form-section">
                  <h5 className="form-section-title mb-4">Account Login</h5>
                  <Form className="login-form" onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label">Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter your email address"
                        className="modern-input"
                        required
                      />
                    </Form.Group>

                    <Form.Group className="pwd-container" style={{ position: "relative" }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <Form.Label className="form-label mb-0">Password</Form.Label>
                        <small>
                          <button
                            type="button"
                            className="forgot-pwd btn btn-link p-0"
                            onClick={() => {
                              // Prefill modal email from known sources
                              const prefill = (userData && userData.email) ? userData.email : formData.email || '';
                              setModalEmail(prefill);
                              setForgotMsg('');
                              setShowForgotModal(true);
                            }}
                            disabled={forgotLoading}
                          >
                            {forgotLoading ? 'Sending...' : 'Forgot password?'}
                          </button>
                        </small>
                      </div>
                      <div className="password-input-wrapper">
                        <Form.Control
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Enter your password"
                          className="modern-input password-input"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="login-password-toggle-btn"
                          title={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                    </Form.Group>
                    {forgotMsg && (
                      <div className="mt-2">
                        <small className="text-muted">{forgotMsg}</small>
                      </div>
                    )}
                    
                    <div className="form-actions text-center">
                      <Button 
                        type="submit" 
                        className="login-btn"
                        disabled={isLoading}
                        size="lg"
                      >
                        {isLoading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Signing you in...
                          </>
                        ) : (
                          <>
                            <FaSignInAlt className="me-2" />
                            Sign In
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                  <div className="mt-3 d-flex justify-content-center">
                    <button
                      type="button"
                      className="link-text btn btn-link p-0"
                      onClick={() => navigate('/')}
                      aria-label="Continue without login"
                    >
                      Continue without login
                    </button>
                  </div>
                </div>
              </Col>

              <Col lg={5} md={12}>
                <div className="side-section">
                  <div className="welcome-content text-center">
                    <div className="welcome-icon">
                      <div className="icon-circle">
                        <FaShieldAlt size={24} className="text-primary" />
                      </div>
                    </div>
                    <h4 className="welcome-title">Secure Login</h4>
                    <p className="welcome-text">
                      Access your account safely with our encrypted login system
                    </p>
                    
                    <div className="social-login">
                      <SocialLogin 
                        className="social-button"
                        onSocialLoginSuccess={handleSocialLoginSuccess}
                        onSocialLoginError={handleSocialLoginError}
                      />
                    </div>
                    
                    <h5 className="divider-text">New to our platform?</h5>
                    
                    <Button
                      variant="primary" 
                      className="signup-btn"
                      onClick={() => navigate('/register')}
                    >
                      Create New Account
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>
       <Modal show={showForgotModal} onHide={() => setShowForgotModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Forgot password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Enter email address</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={modalEmail}
                onChange={(e) => setModalEmail(e.target.value)}
                placeholder="Enter your registered email"
              />
            </Form.Group>

            {forgotMsg && (
              <Alert variant={forgotSuccess ? 'success' : 'danger'}>
                {forgotMsg}
              </Alert>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForgotModal(false)} disabled={forgotLoading}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              await handleForgotPassword(modalEmail.trim());
            }}
            disabled={forgotLoading}
          >
            {forgotLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Sending...
              </>
            ) : (
              'Send Reset Link'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}